import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Crown, Moon, Sun, Activity, Smartphone, Gift,
    LogOut, RefreshCw, ChevronDown, Coins, Sparkles, Zap,
    Gauge, Settings2, ChevronRight, XCircle, CheckCircle, Diamond, Clock, Timer,
    HelpCircle, Download, Loader2, Wand2, BrainCircuit, GraduationCap, Fingerprint, Users2, Bell,
    Type, Volume2, VolumeX, Vibrate, Palette, MessageSquare, ArrowDownToLine, Hash, Keyboard, Eye, EyeOff, RotateCcw, Languages, Eraser
} from 'lucide-react';
import { User, GlobalConfig, PromoCode } from '../../types';
import { hasUnlimitedTokens } from '../../utils/promoEffectsManager';
import { installPWA, isRunningAsPWA, isIOS, canInstall, getInstallState } from '../../utils/pwaInstaller';
import { usePWA } from '../../hooks/usePWA';
import { usePlatformDetection } from '../../hooks/usePlatformDetection';
import { getAPKConfig } from '../admin/APKConfigPanel';
import { getUserRank, getRankGradient, getRankBadge, determineUserTier, isUserMaxTier, isUserPremiumTier, getRankColor, getRankName } from '../../utils/rankUtils';
import BiometricSetupModal from '../BiometricSetupModal';
import UserInfoModal from '../UserInfoModal';
import { hasBiometricEnabled, clearBiometricData } from '../../utils/biometricAuth';
import { downloadLatestAPK, extractGoogleDriveFolderId, openFolderForManualDownload } from '../../utils/googleDriveFolderHelper';
import { extractGoogleDriveFileId, convertToDirectDownloadUrl } from '../../utils/googleDriveHelper';
import { downloadLatestAPKFromFirebase } from '../../services/apkStorageService';

// Token Replenishment Section Component - New Format
const TokenReplenishmentSection: React.FC<{
    user: User;
    config: GlobalConfig;
    isDarkMode: boolean;
}> = ({ user, config, isDarkMode }) => {
    const [tick, setTick] = React.useState(0);

    React.useEffect(() => {
        const timer = setInterval(() => setTick(prev => prev + 1), 1000);
        return () => clearInterval(timer);
    }, []);

    // Determine user's tier using centralized utility
    const userTier = determineUserTier(user);
    const isMaxTier = userTier === 'max';
    const isPremium = userTier === 'premium';

    // Get rank config for styling and values
    const rankConfig = getUserRank(user, config);

    // Get token limit based on tier
    const tokenLimit = isMaxTier
        ? (config?.maxTokenLimit || 50000)
        : isPremium
            ? (config?.premiumTokenLimit || 15000)
            : (config?.freeTokenLimit || 2000);

    // Current token balance
    const currentTokens = user.tokenBalance ?? tokenLimit;

    const replenishmentRate = isMaxTier
        ? (config?.maxRefreshRate || 5000)
        : isPremium
            ? (config?.premiumRefreshRate || 1500)
            : (config?.standardRefreshRate || 200);

    const tierColor = isMaxTier
        ? 'from-purple-500 to-pink-500'
        : isPremium
            ? 'from-amber-500 to-orange-500'
            : 'from-slate-500 to-slate-600';

    // Calculate time until next replenishment - FIXED TO LOOP CONTINUOUSLY
    const now = Date.now();
    const lastRefresh = user?.lastTokenRefresh || now;
    const refreshInterval = (config?.refreshIntervalMinutes || 60) * 60 * 1000; // 1 hour in ms

    // Calculate how much time has passed since last refresh
    const timeSinceRefresh = now - lastRefresh;

    // Calculate time remaining in current hour cycle
    const timeUntilRefresh = refreshInterval - (timeSinceRefresh % refreshInterval);

    // Convert to minutes and seconds
    const minutes = Math.floor(timeUntilRefresh / 60000);
    const seconds = Math.floor((timeUntilRefresh % 60000) / 1000);

    // Calculate percentage for visual bar
    const tokenPercentage = Math.min(100, (currentTokens / tokenLimit) * 100);
    const isLowTokens = tokenPercentage < 20;

    return (
        <div className={`relative overflow-hidden rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
            {/* Gradient accent */}
            <div className={`absolute inset-0 bg-gradient-to-r ${tierColor} opacity-5`} />

            <div className="relative p-3 space-y-3">
                {/* Header with Token Icon */}
                <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded-lg bg-gradient-to-br ${tierColor}`}>
                        <Zap size={14} className="text-white" />
                    </div>
                    <span className={`text-xs font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Token Status
                    </span>
                </div>

                {/* Tokens: current / limit */}
                <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-slate-900/50' : 'bg-white'}`}>
                    <div className="flex items-center justify-between mb-2">
                        <span className={`text-[11px] font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Tokens:
                        </span>
                        <div className="flex items-center gap-1">
                            <span className={`text-xs font-bold ${currentTokens > tokenLimit
                                ? 'text-emerald-500'
                                : isLowTokens
                                    ? 'text-red-500'
                                    : isDarkMode ? 'text-white' : 'text-slate-900'
                                }`}>
                                {currentTokens.toLocaleString()}
                            </span>
                            <span className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>/</span>
                            <span className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                {tokenLimit.toLocaleString()}
                            </span>
                            {currentTokens > tokenLimit && (
                                <span className="text-[8px] font-bold text-emerald-500 bg-emerald-500/20 px-1 py-0.5 rounded ml-0.5">
                                    +{(currentTokens - tokenLimit).toLocaleString()}
                                </span>
                            )}
                        </div>
                    </div>
                    {/* Progress bar */}
                    <div className={`h-2 rounded-full ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                        <div
                            className={`h-full rounded-full transition-all duration-500 ${isLowTokens
                                ? 'bg-gradient-to-r from-red-500 to-orange-500'
                                : `bg-gradient-to-r ${tierColor}`
                                }`}
                            style={{ width: `${Math.min(100, tokenPercentage)}%` }}
                        />
                    </div>
                </div>

                {/* Max Rate */}
                <div className={`flex items-center justify-between p-2 rounded-lg ${isDarkMode ? 'bg-slate-900/50' : 'bg-white'}`}>
                    <span className={`text-[11px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Max Rate:
                    </span>
                    <div className="flex items-center gap-1">
                        <Zap size={12} className={isMaxTier ? 'text-purple-400' : isPremium ? 'text-amber-400' : 'text-slate-400'} />
                        <span className={`text-xs font-bold ${isMaxTier ? 'text-purple-400' : isPremium ? 'text-amber-400' : 'text-slate-400'}`}>
                            {replenishmentRate.toLocaleString()}
                        </span>
                        <span className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            tokens/hr
                        </span>
                    </div>
                </div>

                {/* Next Replenish Countdown */}
                <div className={`flex items-center justify-between p-2 rounded-lg ${isDarkMode ? 'bg-slate-900/50' : 'bg-white'}`}>
                    <span className={`text-[11px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Next replenish in:
                    </span>
                    <div className="flex items-center gap-1">
                        <Clock size={12} className={timeUntilRefresh < 60000 ? 'text-amber-500 animate-pulse' : 'text-emerald-400'} />
                        <span className={`text-sm font-mono font-bold ${timeUntilRefresh < 60000
                            ? 'text-amber-500'
                            : isDarkMode ? 'text-emerald-400' : 'text-emerald-500'
                            }`}>
                            {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Compact Gamification/Leveling Widget for Dropdown
const CompactLevelingWidget: React.FC<{
    isDarkMode: boolean;
    onOpenFullView?: () => void;
}> = ({ isDarkMode, onOpenFullView }) => {
    const [state, setState] = React.useState<any>(null);
    const [summary, setSummary] = React.useState<any>(null);
    const [levelInfo, setLevelInfo] = React.useState<{ level: any; xpProgress: any } | null>(null);

    // Dynamic imports for gamification & subscription
    React.useEffect(() => {
        let unsubscribe: (() => void) | undefined;

        const loadGamification = async () => {
            try {
                const { loadGamificationState, getGamificationSummary, updateStreak, subscribeToGamification } = await import('../../services/gamificationService');
                const { calculateLevel, xpToNextLevel } = await import('../../config/gamificationConfig');

                // Initial load
                // const gamificationState = loadGamificationState(); // subscribe calls it immediately if available

                // Subscribe
                unsubscribe = subscribeToGamification((newState) => {
                    setState(newState);
                    setSummary(getGamificationSummary()); // Summary might need state passing? getGamificationSummary uses currentState internally

                    // Update level info
                    const level = calculateLevel(newState.totalXP);
                    const progress = xpToNextLevel(newState.totalXP);
                    setLevelInfo({ level, xpProgress: progress });
                });

                updateStreak();
            } catch (e) {
                console.warn('[Leveling] Could not load gamification:', e);
            }
        };
        loadGamification();

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, []);

    // Removed the second useEffect as it's now handled in the subscription callback
    // We utilize a separate state for levelInfo to avoid needing the config in render scope if possible, 
    // or just keep using levelInfo state.

    if (!state || !summary || !levelInfo) return null;

    const { level, xpProgress } = levelInfo;

    return (
        <button
            onClick={onOpenFullView}
            className={`w-full relative overflow-hidden rounded-2xl border-2 transition-all duration-300 group ${isDarkMode
                ? 'bg-gradient-to-br from-slate-800/90 via-slate-800/95 to-slate-900/90 border-slate-700/50 hover:border-purple-500/50'
                : 'bg-gradient-to-br from-white via-slate-50/80 to-slate-100/60 border-slate-200 hover:border-purple-400/50'
                } backdrop-blur-sm hover:shadow-xl hover:shadow-purple-500/10 active:scale-[0.98]`}
            style={{
                boxShadow: isDarkMode
                    ? '0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)'
                    : '0 4px 20px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)'
            }}
        >
            {/* Animated gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/5 to-purple-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            <div className="relative p-2.5">
                <div className="flex items-center gap-2.5">
                    {/* Level Badge with Glow */}
                    <div className="relative flex-shrink-0">
                        <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-lg transform group-hover:scale-105 transition-transform duration-300"
                            style={{
                                background: `linear-gradient(135deg, ${level.color}ee, ${level.color})`,
                                boxShadow: `0 4px 15px ${level.color}50, inset 0 1px 0 rgba(255,255,255,0.3)`
                            }}
                        >
                            <span className="drop-shadow">{level.badge}</span>
                        </div>
                        {/* Pulsing glow ring */}
                        <div
                            className="absolute inset-0 rounded-2xl animate-pulse opacity-30"
                            style={{
                                background: `radial-gradient(circle, ${level.color}80 0%, transparent 70%)`,
                                filter: 'blur(10px)'
                            }}
                        />
                    </div>

                    {/* Level Info */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                            <div>
                                <span className={`text-xs font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent`}>
                                    {level.title}
                                </span>
                                <span className={`ml-1 text-[10px] font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Lv.{level.level}
                                </span>
                            </div>
                            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDarkMode ? 'bg-slate-700/70 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                                {xpProgress.current}/{xpProgress.needed} XP
                            </span>
                        </div>
                        {/* Premium XP Progress Bar */}
                        <div className={`relative h-1.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-200/70'}`}>
                            <div
                                className="h-full rounded-full transition-all duration-700 ease-out relative"
                                style={{
                                    width: `${xpProgress.progress}%`,
                                    background: `linear-gradient(90deg, ${level.color}ee, ${level.color})`
                                }}
                            >
                                {/* Shimmer effect */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                            </div>
                        </div>

                        {/* Stats Row - Premium Pills */}
                        <div className="flex items-center gap-1.5 mt-1.5">
                            {/* Streak */}
                            <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full backdrop-blur-sm ${state.streakStatus === 'frozen'
                                ? 'bg-cyan-500/20 border border-cyan-500/30'
                                : summary.streak.current > 0
                                    ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30'
                                    : isDarkMode ? 'bg-slate-700/50 border border-slate-600/30' : 'bg-slate-200/50 border border-slate-300/30'
                                }`}>
                                <div className={`w-3 h-3 rounded-full flex items-center justify-center ${state.streakStatus === 'frozen'
                                    ? 'bg-cyan-500'
                                    : summary.streak.current > 0
                                        ? 'bg-gradient-to-br from-orange-500 to-red-500'
                                        : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'
                                    }`}>
                                    <Sparkles size={7} className={state.streakStatus === 'frozen' || summary.streak.current > 0 ? 'text-white drop-shadow' : isDarkMode ? 'text-slate-500' : 'text-slate-400'} />
                                </div>
                                <span className={`text-[9px] font-bold ${state.streakStatus === 'frozen'
                                    ? 'text-cyan-500'
                                    : summary.streak.current > 0 ? 'text-orange-500' : isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                    }`}>
                                    {state.streakStatus === 'frozen' ? 'Frozen' : summary.streak.current}
                                </span>
                            </div>

                            {/* Total XP */}
                            <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full backdrop-blur-sm ${isDarkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-100/50 border border-amber-200/50'}`}>
                                <Zap size={8} className="text-amber-500 drop-shadow" />
                                <span className={`text-[9px] font-bold text-amber-500`}>
                                    {summary.xp.total > 1000 ? `${(summary.xp.total / 1000).toFixed(1)}k` : summary.xp.total}
                                </span>
                            </div>

                            {/* Achievements */}
                            <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full backdrop-blur-sm ${isDarkMode ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-100/50 border border-purple-200/50'}`}>
                                <Diamond size={8} className="text-purple-500 drop-shadow" />
                                <span className={`text-[9px] font-bold text-purple-500`}>
                                    {summary.achievements.unlocked}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Arrow with hover effect */}
                    <ChevronRight
                        size={14}
                        className={`transform group-hover:translate-x-1 transition-transform duration-300 ${isDarkMode ? 'text-slate-400 group-hover:text-purple-400' : 'text-slate-500 group-hover:text-purple-500'}`}
                    />
                </div>
            </div>
        </button>
    );
};

interface ProfileDropdownProps {
    user: User;
    config: GlobalConfig;
    isDarkMode: boolean;
    messageDensity: 'compact' | 'comfortable' | 'spacious';
    promoCodes: PromoCode[];
    autoBudgetEnabled: boolean;
    budgetPerRequest: number;
    onClose: () => void;
    onToggleDarkMode: () => void;
    onSetDensity: (density: 'compact' | 'comfortable' | 'spacious') => void;
    onOpenUsageModal: () => void;
    onOpenExtendPremium: () => void;
    onOpenTokenPurchase: () => void;
    onLogout: () => void;
    onRedeemCode: (code: string) => Promise<{ success: boolean; message?: string; error?: string }>;
    onToggleAutoBudget: (enabled: boolean) => void;
    onSetBudgetPerRequest: (budget: number) => void;
    onOpenInstallGuide: () => void;
    onOpenSocial?: () => void;
    onOpenSupport?: () => void;
    onOpenQuizConfig?: () => void;
    onOpenMockExamConfig?: () => void;
    onOpenGamification?: () => void; // New: Open the gamification modal
    onOpenNotifications?: () => void; // New: Open notifications
    onOpenDashboard?: () => void; // New: Open user dashboard
    onOpenReferral?: () => void;
    onUserUpdate?: (user: User) => void;
    showToast: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
    user,
    config,
    isDarkMode,
    messageDensity,
    promoCodes,
    autoBudgetEnabled,
    budgetPerRequest,
    onClose,
    onToggleDarkMode,
    onSetDensity,
    onOpenUsageModal,
    onOpenExtendPremium,
    onOpenTokenPurchase,
    onLogout,
    onRedeemCode,
    onToggleAutoBudget,
    onSetBudgetPerRequest,
    onOpenInstallGuide,
    onOpenSocial,
    onOpenSupport,
    onOpenQuizConfig,
    onOpenMockExamConfig,
    onOpenGamification,
    onOpenNotifications,
    onOpenDashboard,
    onOpenReferral,
    showToast
}) => {
    const [redeemCodeInput, setRedeemCodeInput] = useState('');
    const [isRedeemingCode, setIsRedeemingCode] = useState(false);
    const [redeemCodeError, setRedeemCodeError] = useState('');
    const [redeemCodeSuccess, setRedeemCodeSuccess] = useState('');
    const [showRedeemSection, setShowRedeemSection] = useState(false);
    const [showBudgetSettings, setShowBudgetSettings] = useState(false);
    const [showCustomBudget, setShowCustomBudget] = useState(false);
    const [profileTab, setProfileTab] = useState<'account' | 'settings' | 'more'>('account');
    const [customBudgetInput, setCustomBudgetInput] = useState('');
    const [showInstallTooltip, setShowInstallTooltip] = useState(false);
    const [showGenerateMaterials, setShowGenerateMaterials] = useState(false);
    const [showBiometricModal, setShowBiometricModal] = useState(false);
    const [hasBiometric, setHasBiometric] = useState(false);
    const [showUserInfoModal, setShowUserInfoModal] = useState(false);

    // === New Settings State (localStorage-backed) ===
    const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>(() =>
        (localStorage.getItem('luca_font_size') as 'small' | 'medium' | 'large') || 'medium'
    );
    const [soundEnabled, setSoundEnabled] = useState<boolean>(() =>
        localStorage.getItem('luca_sound_enabled') !== 'false'
    );
    const [hapticsEnabled, setHapticsEnabled] = useState<boolean>(() =>
        localStorage.getItem('luca_haptics_enabled') !== 'false'
    );
    const [autoScroll, setAutoScroll] = useState<boolean>(() =>
        localStorage.getItem('luca_auto_scroll') !== 'false'
    );
    const [showTimestamps, setShowTimestamps] = useState<boolean>(() =>
        localStorage.getItem('luca_show_timestamps') === 'true'
    );
    const [sendOnEnter, setSendOnEnter] = useState<boolean>(() =>
        localStorage.getItem('luca_send_on_enter') !== 'false'
    );
    const [streamResponses, setStreamResponses] = useState<boolean>(() =>
        localStorage.getItem('luca_stream_responses') !== 'false'
    );
    const [showAvatars, setShowAvatars] = useState<boolean>(() =>
        localStorage.getItem('luca_show_avatars') !== 'false'
    );
    const [confirmDelete, setConfirmDelete] = useState<boolean>(() =>
        localStorage.getItem('luca_confirm_delete') !== 'false'
    );
    const [autoDeleteDays, setAutoDeleteDays] = useState<'never' | '7' | '30' | '90'>(() =>
        (localStorage.getItem('luca_auto_delete_days') as 'never' | '7' | '30' | '90') || 'never'
    );

    // Helper to persist setting
    const updateSetting = useCallback((key: string, value: string) => {
        localStorage.setItem(key, value);
        window.dispatchEvent(new CustomEvent('luca-setting-change', { detail: { key, value } }));
    }, []);

    // Use platform detection to check if using APK
    const platformInfo = usePlatformDetection();

    // Check if user has biometric enrolled
    useEffect(() => {
        if (user?.id) {
            setHasBiometric(hasBiometricEnabled(user.id));
        }
    }, [user?.id, showBiometricModal]); // Re-check after modal closes

    // Use centralized tier detection
    const userTier = determineUserTier(user);
    const isMaxTier = userTier === 'max';
    const isPremium = userTier === 'premium';

    // Get the correct token limit based on tier (v2.0 economy)
    const tokenLimit = isMaxTier
        ? (config?.maxTokenLimit || 50000)
        : isPremium
            ? (config?.premiumTokenLimit || 15000)
            : (config?.freeTokenLimit || 2000);
    // NEW TOKEN SYSTEM: tokenBalance is the available tokens (can exceed limit through purchases)
    const tokensRemaining = user.tokenBalance ?? tokenLimit;

    const handleRedeemCode = useCallback(async () => {
        if (!redeemCodeInput.trim()) return;

        setIsRedeemingCode(true);
        setRedeemCodeError('');
        setRedeemCodeSuccess('');

        try {
            const result = await onRedeemCode(redeemCodeInput.trim());

            if (result.success) {
                setRedeemCodeSuccess(result.message || '✅ Code redeemed!');
                setRedeemCodeInput('');
                showToast('success', result.message || 'Promo code applied!');
                setTimeout(() => setRedeemCodeSuccess(''), 5000);
            } else {
                setRedeemCodeError(result.error || 'Invalid code');
            }
        } catch (error) {
            setRedeemCodeError('An error occurred');
        } finally {
            setIsRedeemingCode(false);
        }
    }, [redeemCodeInput, onRedeemCode, showToast]);

    // PWA & APK Install handling
    const { isInstallable, promptInstall, isInstalled } = usePWA();
    const platform = usePlatformDetection();
    const [isInstalling, setIsInstalling] = useState(false);
    const [apkConfig, setApkConfig] = useState<{ isEnabled?: boolean; downloadUrl?: string; version?: string } | null>(null);

    // Load APK config
    useEffect(() => {
        const config = getAPKConfig();
        setApkConfig(config);
    }, []);

    // Check if app is already installed (PWA or APK)
    const isAppInstalled = isInstalled || isRunningAsPWA() || platform.isAPK || platform.isPWA;

    const handleInstallApp = useCallback(async () => {
        if (isAppInstalled || isInstalling) return;

        setIsInstalling(true);

        try {
            // Android: Download and install APK if configured
            if (platform.isAndroid && apkConfig?.isEnabled && apkConfig?.downloadUrl) {
                showToast('info', '📥 Downloading Luca APK...');

                // Create download link
                const link = document.createElement('a');
                link.href = apkConfig.downloadUrl;
                link.download = `luca-cpa-v${apkConfig.version || '3.0.0'}.apk`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Wait a moment then show success
                await new Promise(resolve => setTimeout(resolve, 2000));
                showToast('success', '✅ APK downloaded! Open your Downloads to install.');
                onClose();
                return;
            }

            // Desktop/iOS: Install PWA directly
            if (isInstallable) {
                const success = await promptInstall();
                if (success) {
                    showToast('success', '🎉 Luca has been installed!');
                    onClose();
                    return;
                }
            }

            // iOS specific handling
            if (platform.isIOS) {
                showToast('info', '📱 On iOS: Tap Share → Add to Home Screen');
                onClose();
                onOpenInstallGuide();
                return;
            }

            // Fallback: Show install guide
            onClose();
            onOpenInstallGuide();

        } catch (error) {
            console.error('[Install] Error:', error);
            showToast('error', 'Installation failed. Please try again.');
        } finally {
            setIsInstalling(false);
        }
    }, [isAppInstalled, isInstalling, platform, apkConfig, isInstallable, promptInstall, onClose, onOpenInstallGuide, showToast]);

    return (
        <div
            className={`
                absolute right-0 top-full mt-2 z-50 
                w-[340px] max-h-[calc(100vh-100px)] overflow-y-auto
                rounded-2xl shadow-2xl border backdrop-blur-xl
                scrollbar-thin scrollbar-thumb-slate-700/50 scrollbar-track-transparent
                ${isDarkMode
                    ? 'bg-slate-900 border-slate-700/50 shadow-black/40'
                    : 'bg-white border-slate-200 shadow-slate-300/50'
                }
            `}
        >
            {/* Profile Header - Enhanced */}
            <div className={`p-3.5 rounded-t-2xl ${isDarkMode ? 'bg-gradient-to-br from-slate-800/80 to-slate-900/80' : 'bg-gradient-to-br from-slate-50 to-white'}`}>
                <div className="flex items-center gap-3">
                    {/* Avatar with Rank Gradient - Clickable */}
                    <div
                        onClick={() => setShowUserInfoModal(true)}
                        className="relative flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center text-sm font-bold text-white shadow-lg cursor-pointer hover:scale-105 active:scale-95 transition-all duration-200 ring-2 ring-white/10"
                        style={{
                            background: getRankColor(user, config)
                        }}
                    >
                        {(user.username || user.email || 'U')[0].toUpperCase()}
                        {isMaxTier && (
                            <Diamond size={11} className="absolute -top-1.5 -right-1.5 text-pink-300 drop-shadow-lg" />
                        )}
                        {isPremium && (
                            <Crown size={11} className="absolute -top-1.5 -right-1.5 text-amber-300 drop-shadow-lg" />
                        )}
                    </div>

                    {/* User Info with Rank */}
                    <div className="flex-1 min-w-0">
                        <p className={`text-[13px] font-bold truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {user.username}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                            <p
                                className="text-[10px] font-semibold uppercase tracking-wider"
                                style={{ color: getRankColor(user, config) }}
                            >
                                {getRankName(user, config)}
                            </p>
                            <span className={`text-[8px] ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`}>|</span>
                            <p className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {user.email ? user.email.split('@')[0] + '@...' : ''}
                            </p>
                        </div>
                    </div>

                    {/* Token Badge - Enhanced */}
                    <div className={`flex-shrink-0 text-center px-2.5 py-1.5 rounded-xl ${isDarkMode ? 'bg-slate-900/80 ring-1 ring-slate-700/50' : 'bg-white ring-1 ring-slate-200 shadow-sm'}`}>
                        {hasUnlimitedTokens(user) ? (
                            <div className="text-amber-500 font-bold text-sm">∞</div>
                        ) : (
                            <div className={`text-xs font-bold tabular-nums ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                {tokensRemaining.toLocaleString()}
                            </div>
                        )}
                        <div className={`text-[8px] font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>tokens</div>
                    </div>
                </div>
            </div>

            {/* Tab Navigation - Enhanced with icons */}
            <div className={`flex border-b ${isDarkMode ? 'border-slate-700/70 bg-slate-900/50' : 'border-slate-200 bg-slate-50/50'}`}>
                {([{ id: 'account' as const, label: 'Account', icon: <Users2 size={13} /> }, { id: 'settings' as const, label: 'Settings', icon: <Settings2 size={13} /> }, { id: 'more' as const, label: 'More', icon: <Sparkles size={13} /> }]).map((tab) => {
                    const isActive = profileTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setProfileTab(tab.id)}
                            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-semibold tracking-wide transition-all relative ${
                                isActive
                                    ? isDarkMode ? 'text-white' : 'text-slate-900'
                                    : isDarkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'
                            }`}
                        >
                            {tab.icon}
                            {tab.label}
                            {tab.id === 'more' && user && (user.notifications?.length || 0) > 0 && (
                                <span className="absolute top-1.5 right-[calc(50%-28px)] inline-flex w-1.5 h-1.5 bg-red-500 rounded-full" />
                            )}
                            {isActive && (
                                <div className={`absolute bottom-0 left-[15%] right-[15%] h-[2px] rounded-full ${
                                    isDarkMode ? 'bg-blue-400' : 'bg-blue-500'
                                }`} />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* === ACCOUNT TAB === */}
            {profileTab === 'account' && (
                <>
            {/* Quick Actions Row */}
            <div className={`grid grid-cols-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                {/* Notifications */}
                <button
                    onClick={() => { if (onOpenNotifications) { onOpenNotifications(); onClose(); } }}
                    className={`flex flex-col items-center justify-center gap-1 py-3 active:scale-95 transition-all relative ${isDarkMode ? 'hover:bg-slate-700/50 active:bg-slate-700' : 'hover:bg-slate-50 active:bg-slate-100'
                        }`}
                >
                    <div className="relative">
                        <Bell size={18} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />
                        {user && (user.notifications?.length || 0) > 0 && (
                            <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[14px] h-[14px] px-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full shadow-sm animate-pulse">
                                {(user.notifications?.length || 0) > 9
                                    ? '9+'
                                    : user.notifications?.length
                                }
                            </span>
                        )}
                    </div>
                    <span className={`text-[10px] font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Alerts</span>
                </button>
                {/* Theme Toggle */}
                <button
                    onClick={onToggleDarkMode}
                    className={`flex flex-col items-center justify-center gap-1 py-3 active:scale-95 transition-all ${isDarkMode ? 'hover:bg-slate-700/50 active:bg-slate-700' : 'hover:bg-slate-50 active:bg-slate-100'
                        }`}
                >
                    {isDarkMode ? <Moon size={18} className="text-amber-400" /> : <Sun size={18} className="text-amber-500" />}
                    <span className={`text-[10px] font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Theme</span>
                </button>

                {/* Stats */}
                <button
                    onClick={() => {
                        if (onOpenDashboard) {
                            onOpenDashboard();
                        } else {
                            onOpenUsageModal();
                        }
                        onClose();
                    }}
                    className={`flex flex-col items-center justify-center gap-1 py-3 active:scale-95 transition-all ${isDarkMode ? 'hover:bg-slate-700/50 active:bg-slate-700' : 'hover:bg-slate-50 active:bg-slate-100'
                        }`}
                >
                    <Activity size={18} className={isDarkMode ? 'text-blue-400' : 'text-blue-500'} />
                    <span className={`text-[10px] font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Stats</span>
                </button>

                {/* Install/Installed */}
                {isInstalling ? (
                    <div className={`flex flex-col items-center justify-center gap-1 py-3`}>
                        <Loader2 size={18} className={`animate-spin ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                        <span className={`text-[10px] font-medium ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`}>Installing...</span>
                    </div>
                ) : isAppInstalled ? (
                    <div className={`flex flex-col items-center justify-center gap-1 py-3 opacity-50 cursor-not-allowed`}>
                        <CheckCircle size={18} className={isDarkMode ? 'text-emerald-500' : 'text-emerald-500'} />
                        <span className={`text-[10px] font-medium ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Installed</span>
                    </div>
                ) : (
                    <button
                        onClick={async () => {
                            setIsInstalling(true);
                            try {
                                // 🔥 PRIORITY 1: Try Firebase Storage (direct upload) first
                                showToast('info', '🔍 Checking for latest APK...');

                                try {
                                    const firebaseAPK = await downloadLatestAPKFromFirebase();
                                    if (firebaseAPK) {
                                        showToast('success', `✅ ${firebaseAPK.filename} (${firebaseAPK.sizeFormatted}) downloading!`);
                                        onClose();
                                        setIsInstalling(false);
                                        return;
                                    }
                                } catch (firebaseError: any) {
                                    console.log('[Install] Firebase download not available, trying Google Drive...', firebaseError.message);
                                }

                                // 🔄 FALLBACK: Try Google Drive if Firebase fails
                                // Get APK configuration from global config (fallback to localStorage)
                                let apkConfigData = config?.apkConfig;

                                // Fallback to localStorage if not in global config
                                if (!apkConfigData) {
                                    const apkConfigStr = localStorage.getItem('luca_apk_config');
                                    if (apkConfigStr) {
                                        apkConfigData = JSON.parse(apkConfigStr);
                                    }
                                }

                                if (!apkConfigData) {
                                    showToast('error', 'APK download not configured. Please contact admin to upload APK.');
                                    setIsInstalling(false);
                                    return;
                                }

                                if (!apkConfigData.isEnabled) {
                                    showToast('error', 'APK download is currently disabled.');
                                    setIsInstalling(false);
                                    return;
                                }

                                // Try auto-fetch from Google Drive folder
                                if (apkConfigData.autoFetchLatest && apkConfigData.googleDriveFolderId) {
                                    showToast('info', '🔍 Fetching latest APK...');

                                    try {
                                        const folderId = extractGoogleDriveFolderId(apkConfigData.googleDriveFolderId) || apkConfigData.googleDriveFolderId;
                                        const result = await downloadLatestAPK(folderId, apkConfigData.googleDriveApiKey);

                                        if (result) {
                                            showToast('success', `✅ ${result.filename} (${result.size}) downloading!`);
                                            onClose();
                                            setIsInstalling(false);
                                            return;
                                        }
                                    } catch (fetchError: any) {
                                        console.error('[Install] Auto-fetch failed:', fetchError);

                                        // Show the actual error to the user
                                        const errorMsg = fetchError.message || 'Failed to fetch APK';

                                        // Check if it's a permission error
                                        if (errorMsg.includes('access denied') || errorMsg.includes('No APK files found')) {
                                            // Open folder for manual download
                                            const folderId = extractGoogleDriveFolderId(apkConfigData.googleDriveFolderId) || apkConfigData.googleDriveFolderId;
                                            openFolderForManualDownload(folderId);
                                            showToast('error', `⚠️ ${errorMsg}`);
                                            showToast('info', '📂 Opening Google Drive folder for manual download...');
                                            onClose();
                                            setIsInstalling(false);
                                            return;
                                        }
                                        // Show error but continue to fallback URL
                                        showToast('info', `⚠️ Auto-fetch failed: ${errorMsg}. Trying fallback...`);
                                    }
                                }

                                // Fallback to manual URL
                                if (apkConfigData.downloadUrl) {
                                    let downloadUrl = apkConfigData.downloadUrl;
                                    const filename = `luca-cpa-v${apkConfigData.version || 'latest'}.apk`;

                                    const fileId = extractGoogleDriveFileId(downloadUrl);
                                    if (fileId) {
                                        const directUrl = convertToDirectDownloadUrl(downloadUrl);
                                        if (directUrl) {
                                            downloadUrl = directUrl;
                                        }
                                    }

                                    const link = document.createElement('a');
                                    link.href = downloadUrl;
                                    link.download = filename;
                                    link.target = '_blank';
                                    link.rel = 'noopener noreferrer';

                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);

                                    showToast('success', `✅ ${filename} downloading!`);
                                    onClose();
                                } else {
                                    showToast('error', 'No download source configured.');
                                }
                            } catch (error) {
                                console.error('[Install] Download error:', error);
                                showToast('error', 'Download failed. Please try again.');
                            } finally {
                                setIsInstalling(false);
                            }
                        }}
                        className={`flex flex-col items-center justify-center gap-1 py-3 transition-all duration-300 ease-out active:scale-95 group ${isDarkMode ? 'hover:bg-slate-700/50 active:bg-slate-700' : 'hover:bg-slate-50 active:bg-slate-100'
                            }`}
                        aria-label="Install Luca as a Progressive Web App"
                    >
                        <div className="relative">
                            <Download size={18} className={`${isDarkMode ? 'text-purple-400' : 'text-purple-500'} transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-0.5`} />
                            <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                        <span className={`text-[10px] font-medium transition-colors duration-300 ${isDarkMode ? 'text-slate-400 group-hover:text-purple-300' : 'text-slate-500 group-hover:text-purple-600'}`}>Install</span>
                    </button>
                )}
            </div>

            {/* TOKEN REPLENISHMENT SECTION - New Format */}
            <div className={`p-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <TokenReplenishmentSection
                    user={user}
                    config={config}
                    isDarkMode={isDarkMode}
                />
            </div>

            {/* LEVELING DISPLAY - Duolingo-style (clickable to open gamification modal) */}
            {!user.isAnonymous && (
                <div className={`p-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <CompactLevelingWidget
                        isDarkMode={isDarkMode}
                        onOpenFullView={() => {
                            onClose();
                            // Use the callback prop if available, otherwise dispatch event
                            if (onOpenGamification) {
                                onOpenGamification();
                            } else {
                                window.dispatchEvent(new CustomEvent('open-gamification'));
                            }
                        }}
                    />
                </div>
            )}
            </> /* End Account Tab */
            )}

            {/* === SETTINGS TAB === */}
            {profileTab === 'settings' && (
                <>
            {/* --- APPEARANCE SECTION --- */}
            <div className={`px-3 pt-3 pb-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                <p className="text-[9px] font-bold uppercase tracking-widest flex items-center gap-1.5">
                    <Palette size={10} /> Appearance
                </p>
            </div>

            {/* Theme Toggle */}
            <div className={`px-3 py-2.5 flex items-center justify-between ${isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'} transition-colors`}>
                <div className="flex items-center gap-2.5">
                    {isDarkMode ? <Moon size={14} className="text-amber-400" /> : <Sun size={14} className="text-amber-500" />}
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Dark Mode</span>
                </div>
                <button
                    onClick={onToggleDarkMode}
                    className={`relative w-10 h-5.5 rounded-full transition-colors ${isDarkMode ? 'bg-blue-500' : 'bg-slate-300'}`}
                    style={{ width: 40, height: 22 }}
                >
                    <div className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${isDarkMode ? 'translate-x-[20px]' : 'translate-x-[3px]'}`} />
                </button>
            </div>

            {/* Font Size */}
            <div className={`px-3 py-2.5 ${isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'} transition-colors`}>
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                        <Type size={14} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Font Size</span>
                    </div>
                </div>
                <div className={`flex rounded-lg p-0.5 ${isDarkMode ? 'bg-slate-900/50' : 'bg-slate-100'}`}>
                    {(['small', 'medium', 'large'] as const).map((s) => (
                        <button
                            key={s}
                            onClick={() => { setFontSize(s); updateSetting('luca_font_size', s); }}
                            className={`flex-1 py-1.5 rounded-md text-[11px] font-medium transition-all active:scale-95 ${fontSize === s
                                ? isDarkMode ? 'bg-slate-700 text-white shadow' : 'bg-white text-slate-900 shadow-sm'
                                : isDarkMode ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            {s === 'small' ? 'Small' : s === 'medium' ? 'Medium' : 'Large'}
                        </button>
                    ))}
                </div>
            </div>

            {/* Message Density */}
            <div className={`px-3 py-2.5 border-b ${isDarkMode ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-200 hover:bg-slate-50'} transition-colors`}>
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                        <MessageSquare size={14} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Message Density</span>
                    </div>
                </div>
                <div className={`flex rounded-lg p-0.5 ${isDarkMode ? 'bg-slate-900/50' : 'bg-slate-100'}`}>
                    {(['compact', 'comfortable', 'spacious'] as const).map((d) => (
                        <button
                            key={d}
                            onClick={() => onSetDensity(d)}
                            className={`flex-1 py-1.5 rounded-md text-[11px] font-medium transition-all active:scale-95 ${messageDensity === d
                                ? isDarkMode ? 'bg-slate-700 text-white shadow' : 'bg-white text-slate-900 shadow-sm'
                                : isDarkMode ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            {d === 'compact' ? 'Compact' : d === 'comfortable' ? 'Normal' : 'Spacious'}
                        </button>
                    ))}
                </div>
            </div>

            {/* --- CHAT BEHAVIOR SECTION --- */}
            <div className={`px-3 pt-3 pb-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                <p className="text-[9px] font-bold uppercase tracking-widest flex items-center gap-1.5">
                    <Settings2 size={10} /> Chat Behavior
                </p>
            </div>

            {/* Auto-scroll */}
            <div className={`px-3 py-2.5 flex items-center justify-between ${isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'} transition-colors`}>
                <div className="flex items-center gap-2.5">
                    <ArrowDownToLine size={14} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />
                    <div>
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Auto-scroll</span>
                        <p className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Scroll to new messages</p>
                    </div>
                </div>
                <button
                    onClick={() => { const v = !autoScroll; setAutoScroll(v); updateSetting('luca_auto_scroll', String(v)); }}
                    className={`relative rounded-full transition-colors ${autoScroll ? 'bg-blue-500' : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'}`}
                    style={{ width: 40, height: 22 }}
                >
                    <div className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${autoScroll ? 'translate-x-[20px]' : 'translate-x-[3px]'}`} />
                </button>
            </div>

            {/* Stream Responses */}
            <div className={`px-3 py-2.5 flex items-center justify-between ${isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'} transition-colors`}>
                <div className="flex items-center gap-2.5">
                    <Zap size={14} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />
                    <div>
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Stream Responses</span>
                        <p className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Show AI typing in real-time</p>
                    </div>
                </div>
                <button
                    onClick={() => { const v = !streamResponses; setStreamResponses(v); updateSetting('luca_stream_responses', String(v)); }}
                    className={`relative rounded-full transition-colors ${streamResponses ? 'bg-blue-500' : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'}`}
                    style={{ width: 40, height: 22 }}
                >
                    <div className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${streamResponses ? 'translate-x-[20px]' : 'translate-x-[3px]'}`} />
                </button>
            </div>

            {/* Send on Enter */}
            <div className={`px-3 py-2.5 flex items-center justify-between ${isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'} transition-colors`}>
                <div className="flex items-center gap-2.5">
                    <Keyboard size={14} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />
                    <div>
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Send on Enter</span>
                        <p className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Shift+Enter for new line</p>
                    </div>
                </div>
                <button
                    onClick={() => { const v = !sendOnEnter; setSendOnEnter(v); updateSetting('luca_send_on_enter', String(v)); }}
                    className={`relative rounded-full transition-colors ${sendOnEnter ? 'bg-blue-500' : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'}`}
                    style={{ width: 40, height: 22 }}
                >
                    <div className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${sendOnEnter ? 'translate-x-[20px]' : 'translate-x-[3px]'}`} />
                </button>
            </div>

            {/* Show Timestamps */}
            <div className={`px-3 py-2.5 flex items-center justify-between ${isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'} transition-colors`}>
                <div className="flex items-center gap-2.5">
                    <Clock size={14} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Show Timestamps</span>
                </div>
                <button
                    onClick={() => { const v = !showTimestamps; setShowTimestamps(v); updateSetting('luca_show_timestamps', String(v)); }}
                    className={`relative rounded-full transition-colors ${showTimestamps ? 'bg-blue-500' : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'}`}
                    style={{ width: 40, height: 22 }}
                >
                    <div className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${showTimestamps ? 'translate-x-[20px]' : 'translate-x-[3px]'}`} />
                </button>
            </div>

            {/* Show Avatars */}
            <div className={`px-3 py-2.5 flex items-center justify-between border-b ${isDarkMode ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-200 hover:bg-slate-50'} transition-colors`}>
                <div className="flex items-center gap-2.5">
                    {showAvatars ? <Eye size={14} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} /> : <EyeOff size={14} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />}
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Show Avatars</span>
                </div>
                <button
                    onClick={() => { const v = !showAvatars; setShowAvatars(v); updateSetting('luca_show_avatars', String(v)); }}
                    className={`relative rounded-full transition-colors ${showAvatars ? 'bg-blue-500' : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'}`}
                    style={{ width: 40, height: 22 }}
                >
                    <div className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${showAvatars ? 'translate-x-[20px]' : 'translate-x-[3px]'}`} />
                </button>
            </div>

            {/* --- FEEDBACK SECTION --- */}
            <div className={`px-3 pt-3 pb-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                <p className="text-[9px] font-bold uppercase tracking-widest flex items-center gap-1.5">
                    <Volume2 size={10} /> Feedback
                </p>
            </div>

            {/* Sound Effects */}
            <div className={`px-3 py-2.5 flex items-center justify-between ${isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'} transition-colors`}>
                <div className="flex items-center gap-2.5">
                    {soundEnabled ? <Volume2 size={14} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} /> : <VolumeX size={14} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />}
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Sound Effects</span>
                </div>
                <button
                    onClick={() => { const v = !soundEnabled; setSoundEnabled(v); updateSetting('luca_sound_enabled', String(v)); }}
                    className={`relative rounded-full transition-colors ${soundEnabled ? 'bg-blue-500' : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'}`}
                    style={{ width: 40, height: 22 }}
                >
                    <div className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${soundEnabled ? 'translate-x-[20px]' : 'translate-x-[3px]'}`} />
                </button>
            </div>

            {/* Haptic Feedback */}
            <div className={`px-3 py-2.5 flex items-center justify-between border-b ${isDarkMode ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-200 hover:bg-slate-50'} transition-colors`}>
                <div className="flex items-center gap-2.5">
                    <Vibrate size={14} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Haptic Feedback</span>
                </div>
                <button
                    onClick={() => { const v = !hapticsEnabled; setHapticsEnabled(v); updateSetting('luca_haptics_enabled', String(v)); }}
                    className={`relative rounded-full transition-colors ${hapticsEnabled ? 'bg-blue-500' : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'}`}
                    style={{ width: 40, height: 22 }}
                >
                    <div className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${hapticsEnabled ? 'translate-x-[20px]' : 'translate-x-[3px]'}`} />
                </button>
            </div>

            {/* --- DATA & PRIVACY SECTION --- */}
            <div className={`px-3 pt-3 pb-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                <p className="text-[9px] font-bold uppercase tracking-widest flex items-center gap-1.5">
                    <Eraser size={10} /> Data & Privacy
                </p>
            </div>

            {/* Confirm before deleting */}
            <div className={`px-3 py-2.5 flex items-center justify-between ${isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'} transition-colors`}>
                <div className="flex items-center gap-2.5">
                    <CheckCircle size={14} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Confirm Deletions</span>
                </div>
                <button
                    onClick={() => { const v = !confirmDelete; setConfirmDelete(v); updateSetting('luca_confirm_delete', String(v)); }}
                    className={`relative rounded-full transition-colors ${confirmDelete ? 'bg-blue-500' : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'}`}
                    style={{ width: 40, height: 22 }}
                >
                    <div className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${confirmDelete ? 'translate-x-[20px]' : 'translate-x-[3px]'}`} />
                </button>
            </div>

            {/* Auto-delete old chats */}
            <div className={`px-3 py-2.5 border-b ${isDarkMode ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-200 hover:bg-slate-50'} transition-colors`}>
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                        <RotateCcw size={14} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Auto-delete Old Chats</span>
                    </div>
                </div>
                <div className={`flex rounded-lg p-0.5 ${isDarkMode ? 'bg-slate-900/50' : 'bg-slate-100'}`}>
                    {(['never', '7', '30', '90'] as const).map((d) => (
                        <button
                            key={d}
                            onClick={() => { setAutoDeleteDays(d); updateSetting('luca_auto_delete_days', d); }}
                            className={`flex-1 py-1.5 rounded-md text-[10px] font-medium transition-all active:scale-95 ${autoDeleteDays === d
                                ? isDarkMode ? 'bg-slate-700 text-white shadow' : 'bg-white text-slate-900 shadow-sm'
                                : isDarkMode ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            {d === 'never' ? 'Never' : `${d}d`}
                        </button>
                    ))}
                </div>
            </div>

            {/* Reset Settings */}
            <div className={`px-3 py-2.5`}>
                <button
                    onClick={() => {
                        if (confirm('Reset all settings to defaults?')) {
                            const keys = ['luca_font_size', 'luca_sound_enabled', 'luca_haptics_enabled', 'luca_auto_scroll', 'luca_show_timestamps', 'luca_send_on_enter', 'luca_stream_responses', 'luca_show_avatars', 'luca_confirm_delete', 'luca_auto_delete_days'];
                            keys.forEach(k => localStorage.removeItem(k));
                            setFontSize('medium'); setSoundEnabled(true); setHapticsEnabled(true);
                            setAutoScroll(true); setShowTimestamps(false); setSendOnEnter(true);
                            setStreamResponses(true); setShowAvatars(true); setConfirmDelete(true);
                            setAutoDeleteDays('never');
                            showToast('success', 'Settings reset to defaults');
                        }
                    }}
                    className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium transition-all active:scale-[0.97] ${isDarkMode
                        ? 'text-slate-400 hover:text-slate-300 hover:bg-slate-800/50 border border-slate-700/50'
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100 border border-slate-200'
                    }`}
                >
                    <RotateCcw size={12} />
                    Reset All Settings
                </button>
            </div>
            </> /* End Settings Tab */
            )}

            {/* === MORE TAB === */}
            {profileTab === 'more' && (
                <>
            {/* Redeem Code */}
            <div className={`border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <button
                    onClick={() => setShowRedeemSection(!showRedeemSection)}
                    className={`w-full flex items-center justify-between p-3 ${isDarkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'
                        }`}
                >
                    <div className={`flex items-center gap-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        <Gift size={14} />
                        <span className="text-xs font-semibold uppercase tracking-wider">Redeem Code</span>
                    </div>
                    <ChevronDown
                        size={14}
                        className={`transition-transform ${showRedeemSection ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}
                    />
                </button>

                {showRedeemSection && (
                    <div className="px-3 pb-3 space-y-2.5 animate-in slide-in-from-top-2 duration-200">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                placeholder="ENTER CODE"
                                value={redeemCodeInput}
                                onChange={(e) => setRedeemCodeInput(e.target.value.toUpperCase())}
                                className={`flex-1 min-w-0 px-3 py-2.5 text-sm rounded-xl border font-mono tracking-wider ${isDarkMode
                                    ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-500'
                                    : 'bg-white border-slate-300 text-slate-800 placeholder-slate-400'
                                    } focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-all`}
                                maxLength={25}
                                onKeyDown={(e) => e.key === 'Enter' && handleRedeemCode()}
                                autoComplete="off"
                                autoCapitalize="characters"
                            />
                            <button
                                onClick={handleRedeemCode}
                                disabled={!redeemCodeInput.trim() || isRedeemingCode}
                                className={`flex-shrink-0 px-5 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50 active:scale-95 shadow-lg ${isDarkMode
                                    ? 'bg-purple-600 hover:bg-purple-500 active:bg-purple-700 text-white shadow-purple-600/20'
                                    : 'bg-purple-500 hover:bg-purple-400 active:bg-purple-600 text-white shadow-purple-500/20'
                                    }`}
                            >
                                {isRedeemingCode ? <RefreshCw size={16} className="animate-spin" /> : 'Redeem'}
                            </button>
                        </div>
                        {redeemCodeError && (
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDarkMode ? 'bg-red-500/10' : 'bg-red-50'}`}>
                                <XCircle size={14} className="text-red-500 flex-shrink-0" />
                                <p className="text-xs text-red-500 font-medium">{redeemCodeError}</p>
                            </div>
                        )}
                        {redeemCodeSuccess && (
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
                                <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" />
                                <p className="text-xs text-emerald-500 font-medium">{redeemCodeSuccess}</p>
                            </div>
                        )}
                        <p className={`text-[10px] px-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            Enter gift codes or promo codes here
                        </p>
                    </div>
                )}
            </div>

            {/* Free Premium / Referral Button */}
            {onOpenReferral && (
                <button
                    onClick={() => {
                        onClose();
                        onOpenReferral();
                    }}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors active:scale-[0.98] border-b ${isDarkMode
                        ? 'text-indigo-400 hover:bg-indigo-500/10 active:bg-indigo-500/20 border-slate-700'
                        : 'text-indigo-600 hover:bg-indigo-50 active:bg-indigo-100 border-slate-200'
                        }`}
                >
                    <Gift size={16} />
                    Free Premium
                    <span className="ml-1 text-[10px] items-center px-1.5 py-0.5 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-sm font-bold">
                        NEW
                    </span>
                </button>
            )}

            {/* Biometrics Enroll / Delete - Only show for APK users */}
            {platformInfo.isAPK && (
                <button
                    onClick={() => {
                        if (hasBiometric) {
                            // Delete biometric data
                            if (confirm('Are you sure you want to remove biometric authentication? You will need to re-enroll to use biometric login again.')) {
                                clearBiometricData(user.id);
                                setHasBiometric(false);
                                showToast('success', '✅ Biometric authentication removed.');
                            }
                            onClose();
                        } else {
                            // Open setup modal to enroll (don't close dropdown yet)
                            setShowBiometricModal(true);
                        }
                    }}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors active:scale-[0.98] border-b ${hasBiometric
                        ? isDarkMode
                            ? 'text-red-400 hover:bg-red-500/10 active:bg-red-500/20 border-slate-700'
                            : 'text-red-600 hover:bg-red-50 active:bg-red-100 border-slate-200'
                        : isDarkMode
                            ? 'text-purple-400 hover:bg-purple-500/10 active:bg-purple-500/20 border-slate-700'
                            : 'text-purple-600 hover:bg-purple-50 active:bg-purple-100 border-slate-200'
                        }`}
                >
                    <Fingerprint size={16} />
                    {hasBiometric ? 'Delete Biometric Data' : 'Biometrics Enroll'}
                </button>
            )}

            </> /* End More Tab */
            )}

            {/* Customer Support */}
            {onOpenSupport && (
                <button
                    onClick={() => { onOpenSupport(); onClose(); }}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors active:scale-[0.98] border-b ${isDarkMode
                        ? 'text-indigo-400 hover:bg-indigo-500/10 active:bg-indigo-500/20 border-slate-700'
                        : 'text-indigo-600 hover:bg-indigo-50 active:bg-indigo-100 border-slate-200'
                        }`}
                >
                    <HelpCircle size={16} />
                    Customer Support
                </button>
            )}

            {/* Sign Out - Always visible */}
            <button
                onClick={() => { onLogout(); onClose(); }}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors active:scale-[0.98] ${isDarkMode
                    ? 'text-red-400 hover:bg-red-500/10 active:bg-red-500/20'
                    : 'text-red-600 hover:bg-red-50 active:bg-red-100'
                    }`}
            >
                <LogOut size={16} />
                Sign Out
            </button>

            {/* Biometric Setup Modal */}
            <BiometricSetupModal
                isOpen={showBiometricModal}
                onClose={() => {
                    setShowBiometricModal(false);
                    // Refresh biometric state after modal closes
                    if (user?.id) {
                        setHasBiometric(hasBiometricEnabled(user.id));
                    }
                }}
                user={user}
                isDarkMode={isDarkMode}
                showToast={showToast}
            />

            {/* User Info Modal */}
            {showUserInfoModal && (
                <UserInfoModal
                    isOpen={showUserInfoModal}
                    onClose={() => setShowUserInfoModal(false)}
                    user={user}
                    isDarkMode={isDarkMode}
                    config={config}
                />
            )}
        </div>
    );
};

export default ProfileDropdown;

// Add shimmer animation CSS for leveling widget
const shimmerStyle = document.createElement('style');
shimmerStyle.textContent = `
@keyframes shimmer {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
}
.animate-shimmer {
    animation: shimmer 2s infinite;
}
`;
if (typeof document !== 'undefined' && !document.getElementById('profile-animations')) {
    shimmerStyle.id = 'profile-animations';
    document.head.appendChild(shimmerStyle);
}

